(ns practitest.frontend.components.markdown
  (:require
    [practitest.dev.utils :refer [debug-output]]
    ["react-markdown" :default ReactMarkdown]
    ["remark-gfm" :default remarkGfm]
    ["rehype-raw" :default rehypeRaw]
    ["rehype-sanitize" :default rehypeSanitize
     :refer [defaultSchema]]
    ["tributejs" :as tributejs]
    [reagent.core :as reagent]
    [reagent.dom]
    [dommy.core :refer [sel1]]
    [practitest.frontend.common.dom :as dom]
    [practitest.frontend.common.format-utils :as format]
    [practitest.frontend.components.icons :as icons]
    [practitest.frontend.components.tooltip :as tooltip]
    [practitest.frontend.components.preview :as preview]
    [clojure.string :as str]
    [practitest.frontend.common.format-utils :as fu]
    [practitest.frontend.common.i18n :refer [get-translation]]))

;; Use hidden textarea to calculate height to avoid annoying scroll issues
;; inspired by https://github.com/Andarist/react-textarea-autosize/blob/de827c4013dcbaadd3794168e2fed940dc64f09d/src/calculateNodeHeight.ts#L11
(defn- get-hidden-textarea [parent-textarea]
  (let [root-node       (dom/get-root-node parent-textarea)
        hidden-textarea (sel1 root-node "textarea[hidden-textarea=true]")]
    (if (nil? hidden-textarea)
      (let [node (doto (js/document.createElement "textarea")
                   (.setAttribute "hidden-textarea" "true")
                   (.setAttribute "tabindex" "-1")
                   (.setAttribute "aria-hidden" "true")
                   (.setAttribute "class" "form-control"))]
        ;; Set styles
        (doseq [[k v] {:min-height "0"
                       :max-height "none"
                       :height     "auto"
                       :visibility "hidden"
                       :position   "absolute"
                       :overflow   "hidden"
                       :top        "0"
                       :right      "0"
                       :z-index    "-1000"}]
          (.setProperty (.-style node) (name k) v "important"))

        ;; Attach hidden textarea to shadow DOM/body node
        (.appendChild root-node node))

      hidden-textarea)))

(defn- calculate-textarea-height [dom-node max-height value]
  (let [hidden-node (get-hidden-textarea dom-node)]
    (set! (.-value hidden-node) value)
    (+ (min max-height
            (.-scrollHeight hidden-node))
       ;; This 2 is to prevent scrollbar appearance - seems widespread
       2)))

;; Autogrow/autoshrink for textarea
(defn- update-textarea-height [local-state dom-node max-height fullscreen?]
  (when dom-node
    (let [scroll-height (calculate-textarea-height dom-node max-height (.-value dom-node))]
      (swap! local-state assoc :textarea-height (if fullscreen?
                                                  (.-clientHeight dom-node)
                                                  scroll-height))
      (if fullscreen?
        (.removeAttribute dom-node "style")
        (.setProperty (.-style dom-node) "height" (str scroll-height "px")))
      scroll-height)))

(defn- modify-string [s start end value]
  (str (subs s 0 start)
       value
       (subs s end (count s))))

(defn- capture-textarea-state [dom-node]
  {:value           (.-value dom-node)
   :selection-start (.-selectionStart dom-node)
   :selection-end   (.-selectionEnd dom-node)})

(defn- apply-textarea-state! [dom-node state]
  (set! (.-value dom-node) (:value state))
  (when-some [selection-start (:selection-start state)]
    (set! (.-selectionStart dom-node) selection-start))
  (when-some [selection-end (:selection-end state)]
    (set! (.-selectionEnd dom-node) selection-end)))

(defn- conj-limited
  [coll o max-count]
  (vec (take-last max-count (conj coll o))))

(defn- push-undo [state-atom value max-undo]
  (swap! state-atom
         update
         :undo
         conj-limited
         value
         max-undo))

(defn- pop-undo [state-atom redo-state]
  (when-let [new-state (peek (:undo @state-atom))]
    (swap! state-atom
           update
           :undo
           pop)
    (swap! state-atom
           update
           :redo
           conj
           redo-state)
    new-state))

(defn- pop-redo [state-atom]
  (when-let [new-state (peek (:redo @state-atom))]
    (swap! state-atom
           update
           :redo
           pop)
    (swap! state-atom
           update
           :undo
           conj
           new-state)
    new-state))

(defn- update-current-state [state-atom state]
  (swap! state-atom assoc :current-state state))

;; TODO: as props ?
(def max-undo-items 50)
(def max-editor-height 300)

(defn- surround-text-impl [value selection-start selection-end prefix suffix]
  (let [value            (or value "")

        selected-text    (subs value selection-start selection-end)
        prefix-length    (count prefix)
        suffix-length    (count suffix)

        ;; Check if we already have those suffix and prefix
        lookahead-string (subs value
                               (- selection-start prefix-length)
                               (+ selection-end suffix-length))]

    (if (and (>= (count value)
                 (+ prefix-length suffix-length))
             (str/starts-with? lookahead-string prefix)
             (str/ends-with? lookahead-string suffix))
      ;; Undo effect if already prefixed
      [(modify-string value
                      (- selection-start (count prefix))
                      (+ selection-end (count suffix))
                      selected-text)
       ;; We're removing stuff here, hence the minus
       (- selection-start (count prefix))
       (- selection-end (count suffix))]

      ;; Apply effect if not applied yet
      [(modify-string value
                      selection-start
                      selection-end
                      (str prefix selected-text suffix))
       (+ selection-start (count prefix))
       (+ selection-end (count suffix))])))

(defn- surround-text [local-state-static on-change prefix suffix]
  (when-let [textarea (.querySelector (:dom-node @local-state-static) "textarea")]
    (let [selection-start (.-selectionStart textarea)
          selection-end   (.-selectionEnd textarea)
          value           (.-value textarea)

          [new-value
           new-selection-start
           new-selection-end] (surround-text-impl value selection-start selection-end prefix suffix)]
      (push-undo local-state-static
                 (capture-textarea-state textarea)
                 max-undo-items)
      (apply-textarea-state! textarea
                             {:value           new-value
                              :selection-start new-selection-start
                              :selection-end   new-selection-end})
      (.focus textarea)
      (on-change new-value))))

(defn- index-backwards
  [s c start]
  (loop [i (dec start)]
    (if (< i 0)
      -1
      (if (= (aget s i) c)
        i
        (recur (dec i))))))

(defn- replace-current-line [s position new-string]
  (when s
    (let [line-start (index-backwards s "\n" position)
          line-start (if (= line-start -1)
                       0
                       (inc line-start))
          line-end   (.indexOf s "\n" position)
          line-end   (if (= -1 line-end)
                       (count s)
                       (inc line-end))]
      (modify-string s line-start line-end new-string))))

(defn- get-current-line
  [text selection-start]
  (when text
    ;; Search for start
    (let [start (index-backwards text "\n" selection-start)
          end   (.indexOf text "\n" selection-start)
          start (if (= -1 start)
                  0
                  (inc start))
          end   (if (= -1 end)
                  (count text)
                  (inc end))]
      (subs text start end))))

(defn- get-previous-line
  [text selection-start]
  (when text
    (if (<= selection-start 0)
      nil
      ;; Start of current line
      (let [start (index-backwards text "\n" selection-start)]
        (cond
          ;; No new line before
          (= start -1)
          nil

          ;; Newline at start of string - that would be empty previous line
          (= start 0)
          "\n"

          ;; Our line would be current line at that point
          :else
          (get-current-line text start))))))

(def ordered-regex #"^(\d)+. ")
(def ordered-regex-for-newline #"^(\d)+. .+")

(defn- prepend-each-line [local-state-static on-change value mode]
  (when-let [textarea (.querySelector (:dom-node @local-state-static) "textarea")]
    (let [selection-start (.-selectionStart textarea)
          selection-end   (.-selectionEnd textarea)
          value           (or value "")
          multiline?      (not= selection-end selection-start)

          previous-line   (get-previous-line value selection-start)
          counter         (atom (if (some? previous-line)
                                  (if-let [re (re-find ordered-regex previous-line)]
                                    ;; Continue counter if we have previous numbered line
                                    ;; fallback to 0 in case of errors
                                    (inc (fu/try-parse-number2 (second re) 0))
                                    1)
                                  1))

          ;; Little complicated here - nested multiple values destructuring
          [new-value new-selection-start new-selection-end]
          (if multiline?
            ;; Multiline selection handling
            (let [selected-text (subs value selection-start selection-end)
                  ;; see https://clojure.atlassian.net/browse/CLJS-3360 about -1 limit
                  lines         (str/split selected-text #"\n" -1)

                  [new-lines new-selection-start new-selection-end]
                  (reduce
                    (fn [[acc-lines start end] s]
                      (let [prefix        (case mode
                                            :unordered "* "
                                            :ordered (str @counter ". ")
                                            :blockquote "> ")

                            ;; Special handling for ordered mode
                            remove?       (if (= :ordered mode)
                                            (some? (re-find ordered-regex s))
                                            (str/starts-with? s prefix))
                            ;; Override prefix for ordered mode in case of removal
                            prefix        (if (and (= :ordered mode)
                                                   remove?)
                                            (first (re-find ordered-regex s))
                                            prefix)

                            prefix-length (count prefix)]

                        (cond
                          ;; Skip empty lines
                          (str/blank? s)
                          [(conj acc-lines s)
                           start
                           end]

                          ;; Remove prefix on second call (if present)
                          remove?
                          [(conj acc-lines (str/replace-first s prefix ""))
                           start
                           (- end prefix-length)]

                          :else
                          (do
                            (swap! counter inc)
                            [(conj acc-lines (str prefix s))
                             start
                             (+ end prefix-length)]))))
                    [[] selection-start selection-end]
                    lines)

                  new-value     (modify-string value
                                               selection-start
                                               selection-end
                                               (str/join "\n" new-lines))]
              ;; Use those values as return values
              [new-value new-selection-start new-selection-end])
            ;; Single line case
            (let [position      selection-start
                  line          (get-current-line value position)
                  prefix        (case mode
                                  :unordered "* "
                                  :ordered (str @counter ". ")
                                  :blockquote "> ")
                  prefix-length (count prefix)]
              ;; Remove on trigger
              (if (str/starts-with? line prefix)
                [(replace-current-line value selection-start (str/replace-first line prefix ""))
                 (- position prefix-length)
                 (- position prefix-length)]

                [(replace-current-line value selection-start (str prefix line))
                 (+ position prefix-length)
                 (+ position prefix-length)])))]

      (push-undo local-state-static
                 (capture-textarea-state textarea)
                 max-undo-items)
      (apply-textarea-state! textarea
                             {:value           new-value
                              :selection-start new-selection-start
                              :selection-end   new-selection-end})
      (.focus textarea)
      (on-change new-value))))

;; This function is very misleading - you can't use it to actually set textarea field value
;; - it will be overwritten on the next update, you need to call set-field-value to actually
;; update text inside textarea field. This function is used to track cursor position and to
;; provide some kind of accumulator for multiple file links insert
(defn insert-text! [textarea-ref text]
  (when-let [textarea @textarea-ref]
    (let [selection-start (.-selectionStart textarea)
          value           (.-value textarea)
          new-value       (modify-string value selection-start selection-start text)]
      (apply-textarea-state! textarea {:value new-value})
      new-value)))

;; Custom sanitation schema for Markdown preview
(def sanitize-schema
  (clj->js {:strip      ["script"]
            :protocols  {:href ["http" "https" "mailto"]
                         :src  ["http" "https"]}
            :tagNames   [
                         ;; Standard markdown tags
                         "h1" "h2" "h3" "h4" "h5" "h6"
                         "br" "b" "i" "strong" "em" "a"
                         "blockquote" "code" "hr" "li" "ol" "ul"
                         "pre" "img" "div" "p"
                         ;; GFM
                         "del" "input" "table" "tbody" "td" "th" "thead" "tr"
                         ;; Additional tags
                         "font" "sub" "sup"
                         ]
            :attributes {:a     ["href"]
                         :img   ["src"]
                         :font  ["color"]
                         :code  ["class"]
                         :input ["type" "disabled" "checked"]
                         :th    ["style"]
                         :td    ["style"]
                         :ul    ["class"]
                         :li    ["class"]
                         "*"    []}}))

;; I've tried markdown-to-jsx but decided to stick with rehypeRaw + rehypeSanitize
(defn- markdown-preview
  [value]
  [:div.markdown-body
   [:> ReactMarkdown {:remark-plugins [remarkGfm]
                      :rehypePlugins  [rehypeRaw
                                       [rehypeSanitize sanitize-schema]]
                      ;; Override standard image tag with our preview element
                      :components     {:img (fn [args]
                                              (let [src (.-src args)]
                                                (reagent/as-element
                                                  [preview/attachment-with-preview src])))}}
    value]])

(defn- build-users-for-tribute
  [users]
  (apply
    array
    (map
      (fn [user]
        #js {:key   (str (:email user) " - " (:name user))
             :value (:email user)})
      users)))

;; TODO: best way to achieve mentions would be to integrate react-select
;; into overlay (Portal) that would provide consistent UX with filtering
;; portal positioning logic could be quite complex though....
;; see: https://github.com/signavio/react-mentions/blob/79b2c44b4430a76ec8bb16fc94ffc23ae58b9f7c/src/MentionsInput.js#L670
;; for examples of positioning calculations code
;; we're sticking with tribute js for now because it's easier to integrate
(defn tribute-textarea
  [_]
  (let [local-state (atom {})]
    (reagent/create-class
      {:component-did-mount  (fn [this]
                               (let [node (reagent.dom/dom-node this)
                                     props (second (reagent/argv this))
                                     users (:users props)
                                     ;; FIXME: tribute attaches itself to document body and that could lead to issues
                                     ;; with Shadow DOM. Seems to work right now (I think that's because we're already using tributejs)
                                     tribute (tributejs. #js{:values         (build-users-for-tribute users)
                                                             :trigger        "@"
                                                             :selectTemplate (fn [item]
                                                                               (let [original (.-original item)]
                                                                                 (str "[~" (.-value original) "]")))})]
                                 (when node
                                   (reset! local-state {:tributejs           tribute
                                                        :on-tribute-replaced (:on-tribute-replaced props)})
                                   (.attach tribute node)
                                   (.addEventListener node "tribute-replaced"
                                                      (fn [e]
                                                        (when-let [on-tribute-replaced (:on-tribute-replaced @local-state)]
                                                          (on-tribute-replaced e)))))))

       :component-did-update (fn [this old-argv]
                               (let [old-props (second old-argv)
                                     new-props (second (reagent/argv this))]
                                 ;; Update stored on-tribute-replaced callback
                                 (swap! local-state assoc :on-tribute-replaced (:on-tribute-replaced new-props))

                                 ;; Update tribute.js users list on update
                                 (when (not= (:users old-props)
                                             (:users new-props))
                                   (when-let [tribute (:tributejs @local-state)]
                                     (set! (.-values (aget (.-collection tribute) 0))
                                           (build-users-for-tribute (:users new-props)))))))

       :reagent-render       (fn [props]
                               [:textarea (-> props
                                              (dissoc :on-tribute-replaced :users)
                                              (update :style #(merge {:resize "both"}
                                                                     (or % {}))))])})))

;; TODO:
; 2. Move caret back where it was on preview toggle
; 3. Ideally: preview should be scrolled to the same item as in the textarea
; 5. Highlight formatting buttons if selected text matches formatting
(defn markdown-editor [props]
  (let [local-state-updating (reagent/atom {:preview?          false
                                            :textarea-focused? false
                                            :fullscreen?       false
                                            :side-by-side?     false})
        local-state-static   (atom {:undo []
                                    :redo []})
        default-textarea-ref (atom nil)]
    (reagent/create-class
      {:display-name         "markdown-editor"
       :component-did-update (fn [comp old-argv]
                               (update-textarea-height local-state-static
                                                       (.querySelector (reagent.dom/dom-node comp) "textarea")
                                                       max-editor-height
                                                       (:fullscreen? @local-state-updating)))
       :component-did-mount  (fn [comp]
                               (swap! local-state-static assoc :dom-node (reagent.dom/dom-node comp))
                               (update-textarea-height local-state-static
                                                       (.querySelector (reagent.dom/dom-node comp) "textarea")
                                                       max-editor-height
                                                       (:fullscreen? @local-state-updating))

                               (let [[this options] (reagent/argv comp)
                                     textarea (.querySelector (reagent.dom/dom-node comp) "textarea")]
                                 ;; save initial state on component mount
                                 (update-current-state local-state-static
                                                       (if textarea
                                                         (capture-textarea-state textarea)
                                                         {:value (:value options)}))))

       :reagent-render
       (fn [{:keys [value

                    id
                    class

                    editing?
                    focused?

                    ;; events
                    on-change

                    ;; textarea ref support
                    textarea-ref

                    on-drag-over
                    on-drag-leave
                    on-drop

                    on-blur]
             :as   props}]

         (let [{:keys [side-by-side?
                       fullscreen?
                       preview?
                       textarea-focused?]} @local-state-updating


               ;; TODO: detect current formatting mode
               textarea-dom-node (or (and textarea-ref
                                          @textarea-ref)
                                     @default-textarea-ref)

               ]
           (if editing?
             [:div.markdown-editor.form-control
              {:class         (dom/classes-pairs :focused (or focused? textarea-focused?)
                                                 :fullscreen fullscreen?
                                                 class class)
               :on-drag-over  (fn [e]
                                (when on-drag-over
                                  (on-drag-over e)))
               :on-drag-leave (fn [e]
                                (when on-drag-leave
                                  (on-drag-leave e)))
               :on-drop       (fn [e]
                                (when on-drop
                                  (on-drop e)))}

              (let [surround-text-fn     (fn [prefix suffix]
                                           (surround-text local-state-static on-change prefix suffix))
                    prepend-each-line-fn (fn [mode]
                                           (prepend-each-line local-state-static on-change value mode))

                    base-class           "btn-outline-pt-secondary"
                    base-button-fn       (fn [options body]
                                           [tooltip/hover-tooltip
                                            (:title options)

                                            (fn [props]
                                              [:button.btn
                                               (merge
                                                 props

                                                 {:tab-index     -1
                                                  :class         (dom/classes-pairs
                                                                   base-class true
                                                                   "active" (:toggled? options))
                                                  :type          "button"

                                                  ;; Prevent toolbar buttons from grabbing focus
                                                  :on-mouse-down dom/prevent-default}

                                                 (dissoc options :title :toggled?))
                                               body])])]
                [:div.btn-toolbar
                 ;; Text formatting
                 [:div.btn-group.me-2
                  (base-button-fn
                    {:title    (get-translation "markdown.toolbar.bold-text" "Bold text")
                     :on-click (fn [e]
                                 (dom/prevent-default e)
                                 (surround-text-fn "**" "**"))}
                    [icons/svg-type-bold])
                  (base-button-fn
                    {:title    (get-translation "markdown.toolbar.italic-text" "Italic text")
                     :on-click (fn [e]
                                 (dom/prevent-default e)
                                 (surround-text-fn "_" "_"))}
                    [icons/svg-type-italic])
                  (base-button-fn
                    {:title    (get-translation "markdown.toolbar.monospace" "Monospace text")
                     :on-click (fn [e]
                                 (dom/prevent-default e)
                                 (surround-text-fn "`" "`"))}
                    [icons/svg-type])]

                 ;; Per-line item operations
                 [:div.btn-group.me-2
                  (base-button-fn
                    {:title    (get-translation "markdown.toolbar.unordered-list" "Unordered list")
                     :on-click (fn [e]
                                 (prepend-each-line-fn :unordered))}
                    [icons/svg-list-ul])
                  (base-button-fn
                    {:title    (get-translation "markdown.toolbar.ordered-list" "Ordered list")
                     :on-click (fn [e]
                                 (prepend-each-line-fn :ordered))}
                    [icons/svg-list-ol])
                  (base-button-fn
                    {:title    (get-translation "markdown.toolbar.blockquote" "Blockquote")
                     :on-click (fn [e]
                                 (prepend-each-line-fn :blockquote))}
                    [icons/svg-blockquote-left])
                  (base-button-fn
                    {:title    (get-translation "markdown.toolbar.code-block" "Code block")
                     :on-click (fn [e]
                                 (surround-text-fn "```\n" "\n```"))}
                    [icons/svg-code-slash])]

                 ;; preview, full-screen and side-by-side buttons
                 [:div.btn-group
                  ;; Preview
                  (base-button-fn
                    {:class    (dom/classes-pairs :active preview?
                                                  base-class true)
                     :title    (get-translation "markdown.toolbar.preview" "Preview")
                     :on-click (fn [e]
                                 (dom/prevent-default e)
                                 (swap! local-state-updating (fn [o]
                                                               (-> o
                                                                   (update :preview? not)
                                                                   ;; Triggering preview disables side-by-side view
                                                                   (assoc :side-by-side? false)))))}
                    (if preview?
                      [icons/svg-eye-fill]
                      [icons/svg-eye]))

                  (base-button-fn
                    {:title    (get-translation "markdown.toolbar.side-by-side" "Toggle Side by Side")
                     :class    (dom/classes-pairs base-class true
                                                  :active (:side-by-side? @local-state-updating))
                     :on-click (fn [e]
                                 (swap! local-state-updating (fn [o]
                                                               (-> o
                                                                   (update :side-by-side? not)
                                                                   ;; Side-by-Side comes with fullscreen enabled
                                                                   (assoc :fullscreen? true)
                                                                   ;; And preview disabled
                                                                   (assoc :preview? false)))))}
                    [icons/svg-layout-split])
                  (base-button-fn
                    {:title    (get-translation "markdown.toolbar.fullscreen-mode" "Toggle Fullscreen Mode")
                     :class    (dom/classes-pairs base-class true
                                                  :active fullscreen?)
                     :on-click (fn [e]
                                 (swap! local-state-updating (fn [o]
                                                               (let [old-fullscreen? (:fullscreen? o)]
                                                                 (-> o
                                                                     (update :fullscreen? not)
                                                                     ;; Side-by-side is automatically disabled when fullscreen is off
                                                                     (update :side-by-side? #(if old-fullscreen?
                                                                                               false
                                                                                               %)))))))}
                    [icons/svg-arrows-fullscreen])]])

              (let [textarea
                            [tribute-textarea
                             {:value               (or value "")
                              ;; This one is required to prevent scroll from twitching on preview toggle
                              :style               (when-let [height (:textarea-height @local-state-static)]
                                                     {:height (str height "px")})
                              :class               (dom/classes-pairs :fullscreen fullscreen?
                                                                      :sided side-by-side?
                                                                      :form-control true)
                              :on-mouse-down       (fn [e]
                                                     (update-current-state local-state-static
                                                                           (capture-textarea-state (.-target e))))
                              :on-mouse-up         (fn [e]
                                                     (update-current-state local-state-static
                                                                           (capture-textarea-state (.-target e))))

                              :id                  id

                              :ref                 (fn [node]
                                                     (if textarea-ref
                                                       (reset! textarea-ref node)
                                                       (reset! default-textarea-ref node)))

                              :on-key-down         (fn [e]
                                                     ;(js/console.log e)

                                                     (let [key-code  (.-code e)
                                                           meta-key? (.-metaKey e)
                                                           ctrl-key? (.-ctrlKey e)
                                                           textarea  (.-target e)]
                                                       (cond
                                                         ;; Special case for continuing current formatting
                                                         (and (not meta-key?)
                                                              (not ctrl-key?)
                                                              ;; Not using key-code here, because Enter and NumpadEnter are different
                                                              (= "Enter" (.-key e)))
                                                         (let [value           (.-value textarea)
                                                               selection-start (.-selectionStart textarea)
                                                               selection-end   (.-selectionEnd textarea)
                                                               line            (get-current-line value selection-start)
                                                               ordered-check   (re-find ordered-regex-for-newline line)
                                                               other-check     (re-find #"^([*>] ).+" line)]

                                                           (cond
                                                             ;; Special ordered list case
                                                             (some? ordered-check)
                                                             (let [num          (fu/try-parse-number2 (second ordered-check) 0)
                                                                   prefix       (str "\n" (inc num) ". ")
                                                                   new-position (+ selection-start (count prefix))
                                                                   new-value    (modify-string value selection-start selection-end prefix)]
                                                               (dom/prevent-default e)
                                                               (push-undo local-state-static
                                                                          (capture-textarea-state textarea)
                                                                          max-undo-items)
                                                               (apply-textarea-state! (.-target e)
                                                                                      {:value           new-value
                                                                                       :selection-start new-position
                                                                                       :selection-end   new-position})
                                                               (on-change new-value))

                                                             ;; Unordered and blockquote cases
                                                             (some? other-check)
                                                             (let [prefix       (str "\n" (second other-check))
                                                                   new-position (+ selection-start (count prefix))
                                                                   new-value    (modify-string value selection-start selection-end prefix)]
                                                               (dom/prevent-default e)
                                                               (push-undo local-state-static
                                                                          (capture-textarea-state textarea)
                                                                          max-undo-items)
                                                               (apply-textarea-state! (.-target e)
                                                                                      {:value           new-value
                                                                                       :selection-start new-position
                                                                                       :selection-end   new-position})
                                                               (on-change new-value))))

                                                         ;; Undo
                                                         (and (or meta-key?
                                                                  ctrl-key?)
                                                              (= "KeyZ" key-code))
                                                         (do
                                                           (when-let [new-state (pop-undo local-state-static
                                                                                          (capture-textarea-state textarea))]
                                                             (apply-textarea-state! textarea new-state)
                                                             (on-change (:value new-state)))
                                                           (dom/prevent-default e))

                                                         ;; Redo
                                                         (and (or meta-key?
                                                                  ctrl-key?)
                                                              (= "KeyY" key-code))
                                                         (do
                                                           (when-let [new-state (pop-redo local-state-static)]
                                                             (apply-textarea-state! textarea new-state)
                                                             (on-change (:value new-state)))
                                                           (dom/prevent-default e))

                                                         :else
                                                         (update-current-state local-state-static
                                                                               (capture-textarea-state textarea)))))

                              :on-tribute-replaced (fn [e]
                                                     ;; Do not save undo history here
                                                     (update-current-state local-state-static (capture-textarea-state (.-target e)))
                                                     (when on-change
                                                       (on-change (dom/event-value e))))

                              :on-change           (fn [e]
                                                     (push-undo local-state-static
                                                                (:current-state @local-state-static)
                                                                max-undo-items)
                                                     (update-current-state local-state-static (capture-textarea-state (.-target e)))

                                                     (when on-change
                                                       (on-change (dom/event-value e))))

                              :on-focus            (fn [e]
                                                     (swap! local-state-updating assoc :textarea-focused? true))
                              :on-blur             (fn [e]
                                                     (swap! local-state-updating assoc :textarea-focused? false)

                                                     (when on-blur
                                                       (on-blur e)))

                              :users               (:users props)}]

                    preview [:div.preview
                             ;; Set the same height as textarea's
                             {:style {:height (if fullscreen?
                                                "100%"
                                                (:textarea-height @local-state-static))}}
                             (markdown-preview value)]
                    ]

                (if-not preview?
                  (if side-by-side?
                    [:div.side-by-side
                     ;; Side-by-side mode
                     textarea
                     preview]

                    ;; Editor-mode
                    [:div {:class (dom/classes-pairs :fullscreen fullscreen?)}
                     textarea])

                  ;; Preview mode
                  preview
                  ))
              #_[:div.markdown-editor-footer
                 [debug-output @local-state-static]
                 ]]
             ;; Readonly mode
             [:div.form-control.form-control-readonly
              {:class class
               :style {:overflow-y "auto"
                       :max-height (if fullscreen?
                                     "100%"
                                     (or (:textarea-height @local-state-static)
                                         (str (+ max-editor-height 40) "px")))}}
              (markdown-preview value)])))})))
